import { useState, useMemo, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  useListBuilderContacts,
  useListBuilderFilterOptions,
  ListBuilderFilters,
} from '@/hooks/useListBuilder';
import { useUserScope } from '@/hooks/useUserScope';
import { REP_TERRITORY_MAP } from '@/config/filters';
import { ListBuilderFilterPanel } from '@/components/list-builder/ListBuilderFilterPanel';
import { ListBuilderResultsTable } from '@/components/list-builder/ListBuilderResultsTable';
import { ListBuilderActionsPanel } from '@/components/list-builder/ListBuilderActionsPanel';
import { UnifiedContactModal } from '@/components/contacts/UnifiedContactModal';


// ── URL param helpers ──────────────────────────────────────────────
const LB_ARRAY_KEYS: (keyof ListBuilderFilters)[] = [
  'seniorityLevels', 'departments', 'buyerPersonas', 'marketingPersonas',
  'emailStatuses', 'outreachStatuses', 'companyCategories',
  'companyCountries', 'carrModalities', 'modalities', 'employeeRanges',
  'fundingStatuses', 'accountTiers', 'icpTiers', 'clinicalPhases',
  'therapeuticAreas', 'subcategories', 'territories',
];

const LB_BOOL_KEYS: (keyof ListBuilderFilters)[] = [
  'hasEmail', 'hasLinkedIn', 'emailVerifiedOnly', 'decisionMakersOnly', 'topAccountsOnly',
];

function parseLBFiltersFromUrl(params: URLSearchParams): Partial<ListBuilderFilters> {
  const out: Record<string, any> = {};
  const s = params.get('search');
  if (s) out.searchTerm = s;
  for (const k of LB_ARRAY_KEYS) {
    const v = params.get(k as string);
    if (v) out[k as string] = v.split(',').filter(Boolean);
  }
  for (const k of LB_BOOL_KEYS) {
    if (params.has(k as string)) out[k as string] = params.get(k as string) === 'true';
  }
  if (params.has('page')) out.offset = (Number(params.get('page')) - 1) * 50;
  return out;
}

function syncLBFiltersToUrl(filters: ListBuilderFilters, setParams: (p: URLSearchParams, opts?: any) => void) {
  const p = new URLSearchParams();
  if (filters.searchTerm) p.set('search', filters.searchTerm);
  for (const k of LB_ARRAY_KEYS) {
    const v = filters[k] as string[] | undefined;
    if (v && v.length > 0) p.set(k as string, v.join(','));
  }
  for (const k of LB_BOOL_KEYS) {
    if (filters[k]) p.set(k as string, 'true');
  }
  const page = Math.floor((filters.offset || 0) / (filters.limit || 50)) + 1;
  if (page > 1) p.set('page', String(page));
  setParams(p, { replace: true });
}

// ── Component ─────────────────────────────────────────────────────
const ListBuilder = () => {
  const { salesRepName, isAdmin } = useUserScope();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize filters from URL or defaults. Auto-scope to rep's territories
  // (Territory subsumes rep ownership — see territory_rules + territory autoderive trigger).
  const [filters, setFilters] = useState<ListBuilderFilters>(() => {
    const fromUrl = parseLBFiltersFromUrl(searchParams);
    const hasUrlFilters = Object.keys(fromUrl).length > 0;
    const repTerritories = !isAdmin && salesRepName ? REP_TERRITORY_MAP[salesRepName] : undefined;
    return {
      limit: 50,
      offset: 0,
      ...(hasUrlFilters ? fromUrl : {
        territories: repTerritories ? [...repTerritories] : undefined,
      }),
    };
  });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [initialized, setInitialized] = useState(!!salesRepName || isAdmin);

  // Contact Detail Modal state
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactModalId, setContactModalId] = useState<string | null>(null);

  // Sync filters → URL whenever filters change (skip first render)
  const skipFirstSync = useRef(true);
  useEffect(() => {
    if (skipFirstSync.current) { skipFirstSync.current = false; return; }
    syncLBFiltersToUrl(filters, setSearchParams);
  }, [filters, setSearchParams]);

  // Handle async user scope loading (only if URL didn't already set territories).
  useEffect(() => {
    if (initialized) return;
    if (salesRepName) {
      if (!filters.territories || filters.territories.length === 0) {
        const repTerritories = REP_TERRITORY_MAP[salesRepName];
        if (repTerritories) {
          setFilters((prev) => ({ ...prev, territories: [...repTerritories] }));
        }
      }
      setInitialized(true);
    } else if (isAdmin) {
      setInitialized(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot init; filters.territories must not retrigger
  }, [salesRepName, isAdmin, initialized]);

  const { data: result, isLoading } = useListBuilderContacts(filters);
  const { data: filterOptions } = useListBuilderFilterOptions();

  const contacts = useMemo(() => result?.data || [], [result?.data]);
  const totalCount = result?.totalCount ?? 0;
  const pageSize = filters.limit || 50;
  const pageIndex = Math.floor((filters.offset || 0) / pageSize);

  const selectedContacts = useMemo(() => {
    return contacts.filter((c) => selectedIds.has(c.id));
  }, [contacts, selectedIds]);

  const handlePageSizeChange = (size: number) => {
    setFilters((prev) => ({ ...prev, limit: size, offset: 0 }));
  };

  const handlePageIndexChange = (index: number) => {
    setFilters((prev) => ({ ...prev, offset: index * (prev.limit || 50) }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleClearSelection = () => {
    setSelectedIds(new Set());
  };

  return (
    <div className="p-6 md:p-8 space-y-6">
      <div className="space-y-1">
        <h1 className="text-3xl font-bold tracking-tight">List Builder</h1>
        <p className="text-muted-foreground">Build targeted contact lists for outreach campaigns</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-1">
          <ListBuilderFilterPanel
            filters={filters}
            onFiltersChange={setFilters}
            filterOptions={filterOptions}
            totalCount={totalCount}
          />
        </div>

        <div className="lg:col-span-3 space-y-4">
          <ListBuilderActionsPanel
            selectedContacts={selectedContacts}
            selectedCount={selectedIds.size}
            filters={filters}
            totalCount={totalCount}
            onClearSelection={handleClearSelection}
          />
          <ListBuilderResultsTable
            contacts={contacts}
            totalCount={totalCount}
            isLoading={isLoading}
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            pageSize={pageSize}
            pageIndex={pageIndex}
            onPageSizeChange={handlePageSizeChange}
            onPageIndexChange={handlePageIndexChange}
            onContactClick={(id) => { setContactModalId(id); setContactModalOpen(true); }}
          />
        </div>
      </div>

      {/* Contact Detail Modal */}
      <UnifiedContactModal
        contactId={contactModalId}
        open={contactModalOpen}
        onOpenChange={setContactModalOpen}
      />
    </div>
  );
};

export default ListBuilder;
